const defaultColors = [
  '#5470C6', // blue
  '#91CC75', // green
  '#EE6666', // red
  '#FAC858', // yellow
];

const defaultGridPadding = {
  left: 50,
  right: 20,
  top: 70,
  bottom: 100,
};

let lastPointerClientX = null;
let lastPointerClientY = null;
let pointerTrackingBound = false;
let cachedPageWrapper = null;

const ensurePointerTracking = () => {
  if (pointerTrackingBound || typeof window === 'undefined') return;
  window.addEventListener(
    'mousemove',
    (event) => {
      lastPointerClientX = event.clientX;
      lastPointerClientY = event.clientY;
    },
    { passive: true },
  );
  pointerTrackingBound = true;
};

export const createChartOption = ({
  title = null, // Chart title
  titleTop, // Optional top offset for title
  xAxisLabels = null, // X-axis labels
  xAxisLabelType, // Type of x-axis labels: 'time', 'category', etc
  xAxisLabelFormatter, // Optional custom formatter for x-axis labels
  yAxisLabel = null, // Y-axis label
  series = [], // Array of series objects (bar chart, line chart, etc.)
  gridPadding = defaultGridPadding, // Padding around the chart grid (uses top, right, bottom, left)
  tooltipFormatter, // Optional custom tooltip formatter function
  xAxisRotate = 0, // Rotation angle for x-axis labels
  xAxisLabelFontSize, // Optional font size for x-axis labels
  xAxisLabelHideOverlap, // Optional overlap handling for x-axis labels
  xAxisLabelInterval, // Optional interval for x-axis labels
  xAxisLabelMargin, // Optional margin for x-axis labels
  dataZoomEnabled = true, // Whether to enable data zoom
  markLineData = [], // Data for mark lines
  minValue, // Minimum value for x-axis
  maxValue, // Maximum value for x-axis
}) => {
  if (series.length === 0) {
    console.error('At least one series must be provided.');
    return null;
  }

  if (markLineData.length > 0) {
    // Find the Y-axis range to calculate 8% height
    let maxY = 0;
    series.forEach((s) => {
      if (Array.isArray(s.data)) {
        s.data.forEach((val) => {
          const y = typeof val === 'number' ? val : val[1] || 0;
          if (y > maxY) maxY = y;
        });
      }
    });

    const lineHeight = maxY * 0.08; // 8% of the max Y value

    // Convert markLineData to two-point format for partial-height lines at bottom
    //console.log("Marked Line Data:", markLineData);
    const formattedMarkLineData = markLineData.map((item) => [
      {
        xAxis: item.xAxis,
        yAxis: 0, // Start at bottom
        lineStyle: {
          type: 'solid',
          width: 2,
          color: item.color || '#ff0000',
        },
      },
      {
        xAxis: item.xAxis,
        yAxis: lineHeight, // Extend 8% of chart height from bottom
        lineStyle: {
          type: 'solid',
          width: 2,
          color: item.color || '#ff0000',
        },
      },
    ]);

    series[0] = {
      ...series[0],
      markLine: {
        symbol: 'none',
        silent: true,
        animation: false,
        data: formattedMarkLineData,
      },
    };
  }

  return {
    color: defaultColors,
    title: {
      text: title,
      left: 'center', // Center the title horizontally
      top: titleTop ?? 0, // Position the title at the top
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis', // Show tooltip when hovering any point on the x-axis
      triggerOn: 'mousemove',
      showDelay: 0,
      hideDelay: 0,
      transitionDuration: 0,
      axisPointer: {
        type: 'cross', // Creates the vertical and horizontal lines that follow the mouse cursor
        animation: false,
      },

      // Keep tooltip contained within the chart container to avoid clipping
      appendToBody: true,
      // Do not confine to chart view; we clamp manually against page-content bounds in `position`.
      confine: false,
      // Allow mouse to enter the tooltip so it doesn't disappear when hovered
      enterable: true,
      renderMode: 'html',
      className: 'echart-tooltip-portal',
      position: (point, params, dom, rect, size) => {
        ensurePointerTracking();
        const [x, y] = point;
        const [contentWidth, contentHeight] = size.contentSize;
        const padding = 8;
        const cursorGap = 22;

        // Keep horizontal placement chart-local, but allow vertical overflow
        // up/down until the page content wrapper bounds.
        let lowerBoundX = padding;
        let upperBoundX = size.viewSize[0] - padding;
        let lowerBoundY = padding;
        let upperBoundY = size.viewSize[1] - padding;

        if (
          lastPointerClientX != null &&
          lastPointerClientY != null &&
          typeof document !== 'undefined'
        ) {
          const chartLeft = lastPointerClientX - x;
          const chartTop = lastPointerClientY - y;

          // Re-query only when the cached wrapper is gone from the DOM
          if (!cachedPageWrapper || !document.body.contains(cachedPageWrapper)) {
            const hoverTarget = document.elementFromPoint(lastPointerClientX, lastPointerClientY);
            cachedPageWrapper =
              hoverTarget?.closest?.('.page-content-wrapper') ||
              hoverTarget?.closest?.('.page-scroll-area') ||
              document.querySelector('.page-content-wrapper');
          }

          if (cachedPageWrapper) {
            const bounds = cachedPageWrapper.getBoundingClientRect();
            lowerBoundY = bounds.top - chartTop + padding;
            upperBoundY = bounds.bottom - chartTop - padding;
          }
        }

        const preferRight = x < (lowerBoundX + upperBoundX) / 2;
        const preferBelow = y < (lowerBoundY + upperBoundY) / 2;

        let left = preferRight ? x + cursorGap : x - contentWidth - cursorGap;
        let top = preferBelow ? y + cursorGap : y - contentHeight - cursorGap;

        left = Math.max(lowerBoundX, Math.min(left, upperBoundX - contentWidth));
        top = Math.max(lowerBoundY, Math.min(top, upperBoundY - contentHeight));

        const pointerInsideTooltip =
          x >= left - 2 &&
          x <= left + contentWidth + 2 &&
          y >= top - 2 &&
          y <= top + contentHeight + 2;

        if (pointerInsideTooltip) {
          const altLeft = preferRight ? x - contentWidth - cursorGap : x + cursorGap;
          const altTop = preferBelow ? y - contentHeight - cursorGap : y + cursorGap;
          const clampedAltLeft = Math.max(
            lowerBoundX,
            Math.min(altLeft, upperBoundX - contentWidth),
          );
          const clampedAltTop = Math.max(
            lowerBoundY,
            Math.min(altTop, upperBoundY - contentHeight),
          );

          const altInside =
            x >= clampedAltLeft - 2 &&
            x <= clampedAltLeft + contentWidth + 2 &&
            y >= clampedAltTop - 2 &&
            y <= clampedAltTop + contentHeight + 2;

          if (!altInside) {
            left = clampedAltLeft;
            top = clampedAltTop;
          }
        }

        const availableHeight = Math.max(120, Math.floor(upperBoundY - top));
        const tooltipContent = dom?.querySelector?.('.echart-tooltip-content');
        if (tooltipContent) {
          tooltipContent.style.maxHeight = `${availableHeight}px`;
          tooltipContent.style.overflowY = 'auto';
        }

        return [left, top];
      },
      ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}), // Custom tooltip
    },
    legend: {
      type: 'scroll', // Adds pagination if too many legend items
      top: '12%', // Position legend below the title
      pageIconSize: 20, // Larger icon size increases clickable area
      pageButtonGap: 12, // Gap between the pagination buttons and other legend controls
      pageButtonItemGap: 8, // Spacing between individual pagination buttons
      padding: [16, 6, 6, 6], // Legend bounding-box padding [top, right, bottom, left]
    },
    grid: { ...gridPadding },
    dataZoom: dataZoomEnabled
      ? [
          {
            type: 'slider', // Enables a slider at the bottom for zooming
            show: true,
            height: 20,
            left: 5,
            right: 5,
            bottom: 10,
          },
          {
            type: 'inside', // Enables zooming via mouse wheel or touch gestures
          },
        ]
      : [],
    xAxis: {
      type: xAxisLabelType, // Time-based data, categorical data, numeric data, etc.
      data: xAxisLabels,
      axisLabel: {
        rotate: xAxisRotate,
        formatter: xAxisLabelFormatter, //Change what labels read
        ...(xAxisLabelFontSize ? { fontSize: xAxisLabelFontSize } : {}),
        ...(typeof xAxisLabelHideOverlap === 'boolean'
          ? { hideOverlap: xAxisLabelHideOverlap }
          : {}),
        ...(xAxisLabelInterval !== undefined ? { interval: xAxisLabelInterval } : {}),
        ...(xAxisLabelMargin !== undefined ? { margin: xAxisLabelMargin } : {}),
      },
      min: minValue ? minValue : null,
      max: maxValue ? maxValue : null,
      axisLine: { show: true }, // Show x-axis line
      axisTick: { show: false }, // Hide x-axis ticks (little dashes that look like: -----)
      splitLine: { show: false }, // Hide vertical grid lines
    },
    yAxis: {
      type: 'value', // Numerical data
      name: yAxisLabel, // Labels the y-axis
      nameLocation: 'middle',
      nameGap: 50,
      nameTextStyle: {
        fontSize: 12,
        fontWeight: 'bold',
      },
      axisLine: { show: true }, // Show y-axis line
      axisTick: { show: false }, // Hide y-axis ticks (little dashes that look like: -----)
      splitLine: { show: true }, // Show horizontal grid lines
    },
    series: series,
  };
};
